/**
 * Dateien zum Anwender bringen.
 *
 * Anhaenge im Verlauf und im Reiter, Dateien der Wissensdatenbank, Berichte
 * und Exporte -- das Herunterladen ist ueberall dasselbe. Es steht hier und
 * nicht an jeder Stelle einzeln, sonst laufen Dateiname, Freigabe des
 * Verweises und Fehleranzeige irgendwann auseinander.
 */

import { useMutation } from '@tanstack/react-query';

/**
 * Holt eine Datei und uebergibt sie dem Browser.
 *
 * Ueber `fetch` und einen Objektverweis statt ueber einen einfachen Link: Ein
 * `<a href>` traegt den `Authorization`-Kopf nicht mit. Der Verweis wird
 * unmittelbar wieder freigegeben -- sonst haelt der Browser die Datei bis zum
 * Neuladen der Seite im Speicher.
 *
 * Angezeigt wird nichts: Der Server liefert jeden Anhang ausschliesslich als
 * Download aus (`Content-Disposition: attachment`, `nosniff`). Eine
 * hochgeladene HTML-Datei liefe sonst im Ursprung der Anwendung.
 *
 * Der Abruf wird uebergeben statt der Kennung: Die Pfade unterscheiden sich,
 * das Herunterladen nicht.
 */
export async function saveDownload(
  fetchFile: () => Promise<{ blob: Blob; filename: string | null }>,
  fallbackName: string,
): Promise<void> {
  const { blob, filename } = await fetchFile();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename ?? fallbackName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Dasselbe als Mutation -- fuer jeden Knopf, der eine Datei holt.
 *
 * Ein blosser Aufruf verschluckt den Fehler, und der Knopf sieht dann aus,
 * als taete er nichts. Ueber die Mutation steht der Fehler samt
 * Fehlerkennung fuer ``ErrorNotice`` bereit, und ``isPending`` sperrt den
 * Knopf waehrend des Abrufs.
 */
export function useFileDownload() {
  return useMutation({
    mutationFn: ({
      fetchFile,
      fallbackName,
    }: {
      fetchFile: () => Promise<{ blob: Blob; filename: string | null }>;
      fallbackName: string;
    }) => saveDownload(fetchFile, fallbackName),
  });
}

/** Groessenangabe in der Einheit, die ein Mensch erwartet. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
